// Poketrace Price Service - Core module
// Handles ID resolution, price fetching, and Supabase caching.
// Multi-source pricing: Poketrace + TCGdex + Gemini AI analysis.

import { supabase } from '../lib/supabase';
import { fetchTCGdexPricing, getTCGdexBestPrice } from './tcgdexPricing';
import { fetchJustTCGPricing } from './justTCGPricing';

const API_BASE = 'https://api.poketrace.com/v1';
const API_KEY = process.env.REACT_APP_POKETRACE_API_KEY;

// !! DO NOT CHANGE without asking Chase first !!
// Bump this integer when search/pricing logic changes to auto-invalidate stale cache.
// All cached rows with a different version are treated as stale and re-fetched.
// Stored in Supabase as "v{N}" (e.g. "v2") in the research_version column.
export const RESEARCH_CACHE_VERSION = 3;

// Grade mapping: user-facing grade string -> Poketrace tier key
const CONDITION_MAP = {
  'NM': 'NEAR_MINT',
  'Near Mint': 'NEAR_MINT',
  'nm': 'NEAR_MINT',
  'LP': 'LIGHTLY_PLAYED',
  'Lightly Played': 'LIGHTLY_PLAYED',
  'lp': 'LIGHTLY_PLAYED',
  'MP': 'MODERATELY_PLAYED',
  'Moderately Played': 'MODERATELY_PLAYED',
  'mp': 'MODERATELY_PLAYED',
  'HP': 'HEAVILY_PLAYED',
  'Heavily Played': 'HEAVILY_PLAYED',
  'hp': 'HEAVILY_PLAYED',
  'DMG': 'DAMAGED',
  'Damaged': 'DAMAGED',
  'damaged': 'DAMAGED',
  'Ungraded': 'NEAR_MINT',
  'ungraded': 'NEAR_MINT',
};

// Condition multipliers as fallback when a specific tier is missing from Poketrace
const CONDITION_MULTIPLIERS = {
  'NEAR_MINT': 1.0,
  'LIGHTLY_PLAYED': 0.8,
  'MODERATELY_PLAYED': 0.6,
  'HEAVILY_PLAYED': 0.4,
  'DAMAGED': 0.25,
};

// Legacy condition multipliers (same keys as old priceService)
const LEGACY_CONDITION_MULTIPLIERS = {
  'nm': 1.0,
  'lp': 0.85,
  'mp': 0.65,
  'hp': 0.45,
  'damaged': 0.20,
};

// Raw condition tiers (non-graded)
const RAW_TIERS = ['NEAR_MINT', 'LIGHTLY_PLAYED', 'MODERATELY_PLAYED', 'HEAVILY_PLAYED', 'DAMAGED'];

// Graded sort order
const GRADE_SORT = [
  'PSA_10', 'PSA_9', 'PSA_8', 'PSA_7', 'PSA_6', 'PSA_5', 'PSA_4', 'PSA_3', 'PSA_2', 'PSA_1',
  'CGC_10', 'CGC_9_5', 'CGC_9', 'CGC_8_5', 'CGC_8', 'CGC_7_5', 'CGC_7', 'CGC_6_5', 'CGC_6',
  'CGC_5_5', 'CGC_5', 'CGC_4_5', 'CGC_4', 'CGC_3_5', 'CGC_3', 'CGC_2_5', 'CGC_2', 'CGC_1_5', 'CGC_1',
  'BGS_10', 'BGS_9_5', 'BGS_9', 'BGS_8_5', 'BGS_8', 'BGS_7_5', 'BGS_7', 'BGS_6_5', 'BGS_6',
  'BGS_5_5', 'BGS_5', 'BGS_4_5', 'BGS_4', 'BGS_3_5', 'BGS_3', 'BGS_2_5', 'BGS_2',
  'SGC_10', 'SGC_9_5', 'SGC_9', 'SGC_8_5', 'SGC_8', 'SGC_7_5', 'SGC_7', 'SGC_6_5', 'SGC_6',
  'SGC_5_5', 'SGC_5', 'SGC_4_5', 'SGC_4', 'SGC_3_5', 'SGC_3', 'SGC_2_5', 'SGC_2',
  'ACE_10', 'ACE_9', 'ACE_8', 'ACE_7', 'ACE_6', 'ACE_5', 'ACE_4', 'ACE_3', 'ACE_2',
  'TAG_10', 'TAG_9_5', 'TAG_9', 'TAG_8_5', 'TAG_8',
];

// ---- Poketrace API ----

/**
 * Convert a user-facing grade like "PSA 10" or "CGC 9.5" to a Poketrace key like "PSA_10" or "CGC_9_5"
 */
export function gradeToPoketraceKey(grade) {
  if (!grade) return null;

  // Check if it's a raw condition
  const conditionKey = CONDITION_MAP[grade];
  if (conditionKey) return conditionKey;

  // Also check case-insensitive condition keywords
  const gradeLower = grade.toLowerCase();
  if (gradeLower.includes('damaged') || gradeLower.includes('dmg')) return 'DAMAGED';
  if (gradeLower.includes('heavily') || gradeLower === 'hp') return 'HEAVILY_PLAYED';
  if (gradeLower.includes('moderately') || gradeLower === 'mp') return 'MODERATELY_PLAYED';
  if (gradeLower.includes('lightly') || gradeLower === 'lp') return 'LIGHTLY_PLAYED';
  if (gradeLower.includes('near mint') || gradeLower.includes('mint') || gradeLower === 'nm') return 'NEAR_MINT';

  // Graded: "PSA 10" -> "PSA_10", "CGC 9.5" -> "CGC_9_5"
  const parts = grade.trim().split(/\s+/);
  if (parts.length < 2) return null;

  const company = parts[0].toUpperCase();
  const number = parts.slice(1).join('').replace(/\./g, '_');
  return `${company}_${number}`;
}

/**
 * Check if a grade string represents a graded card (vs raw condition)
 */
export function isGradedCard(grade) {
  if (!grade) return false;
  const gradeLower = grade.toLowerCase();
  return gradeLower.includes('psa') ||
         gradeLower.includes('cgc') ||
         gradeLower.includes('bgs') ||
         gradeLower.includes('sgc') ||
         gradeLower.includes('tag') ||
         gradeLower.includes('ace');
}

/**
 * Normalize card number for comparison: "4/102" and "004/102" both become "4/102"
 */
function normalizeCardNumber(num) {
  if (!num) return '';
  // Strip leading zeros from each part: "005/025" -> "5/25"
  return num.split('/').map(p => p.replace(/^0+/, '') || '0').join('/').toLowerCase();
}

/**
 * Search Poketrace for a card by name and match by card number + set
 */
async function searchPoketraceCard(cardName, cardNumber, setName, variant) {
  const perfLabel = `[Perf] searchPoketraceCard: ${cardName}`;
  console.time(perfLabel);
  if (!API_KEY) {
    console.warn('[Poketrace] API key not configured');
    console.timeEnd(perfLabel);
    return null;
  }

  // !! DO NOT CHANGE search query construction without asking Chase first !!
  // Name + set + number keeps Poketrace results specific. Without the number,
  // common names like "Lugia" return wrong cards (e.g. Neo Genesis instead of Skyridge).
  // Poketrace caps results at 20, so specificity matters.
  let searchQuery = cardName;
  if (setName) searchQuery += ` ${setName}`;
  if (cardNumber) searchQuery += ` ${cardNumber.split('/')[0]}`;
  const res = await fetch(`${API_BASE}/cards?search=${encodeURIComponent(searchQuery)}`, {
    headers: { 'X-API-Key': API_KEY }
  });

  if (!res.ok) {
    console.error('[Poketrace] Search failed:', res.status);
    return null;
  }

  const data = await res.json();
  const cards = data.data || data.cards || data || [];

  if (!Array.isArray(cards) || cards.length === 0) return null;

  // Prefer US market cards over EU
  const usCards = cards.filter(c => c.market === 'US');
  const searchPool = usCards.length > 0 ? usCards : cards;

  const normalizedNumber = normalizeCardNumber(cardNumber);
  const variantLower = (variant || '').toLowerCase();
  const is1stOrShadowless = variantLower.includes('1st') || variantLower.includes('first') || variantLower.includes('shadowless');

  // Score candidates: higher is better match
  const scoredCandidates = [];

  // Log all candidates so we can see what Poketrace returned
  console.log(`[Poketrace] Search returned ${searchPool.length} candidates for "${searchQuery}":`);
  searchPool.forEach((c, i) => {
    console.log(`  [${i}] #${c.cardNumber} | set: ${c.set?.name} | variant: ${c.variant} | market: ${c.market}`);
  });

  for (const card of searchPool) {
    const candidateNumber = normalizeCardNumber(card.cardNumber);
    const candidateSet = (card.set?.name || '').toLowerCase();
    const candidateVariant = (card.variant || '').toLowerCase();
    const targetSet = (setName || '').toLowerCase();

    let score = 0;

    // Card number match - handle "4" vs "4/102" (TCGdex localId vs Poketrace full number)
    if (normalizedNumber && candidateNumber === normalizedNumber) {
      score += 10;
    } else if (normalizedNumber && candidateNumber.split('/')[0] === normalizedNumber.split('/')[0]) {
      score += 10; // Match on card number portion (e.g. "4" matches "4/102")
    } else if (normalizedNumber) {
      continue; // Skip if we have a number and it doesn't match
    }

    // Set name match - handle Poketrace prefixed names like "SM - Ultra Prism", "SWSH03: Darkness Ablaze"
    // while still avoiding "Team Rocket" matching "Team Rocket Returns"
    if (targetSet && candidateSet) {
      const candidateBase = candidateSet.replace(/\s*\(.*\)/, '').trim();
      if (candidateBase === targetSet || candidateSet === targetSet) {
        score += 5;
      } else if (candidateBase.endsWith(targetSet) || targetSet.includes(candidateBase)) {
        score += 3;
      } else {
        continue; // Skip if set doesn't match at all
      }
    }

    // Variant matching: 1st Edition / Shadowless
    const candidate1stOrShadowless = candidateSet.includes('shadowless') ||
      candidateVariant.includes('1st') ||
      candidateVariant.includes('first') ||
      candidateVariant.includes('shadowless');

    if (is1stOrShadowless) {
      // User has 1st Edition / Shadowless -- prefer matching entries
      if (candidate1stOrShadowless) {
        score += 20; // Strong preference for 1st ed / shadowless entry
      } else {
        score -= 10; // Penalize unlimited when user has 1st ed
      }
    } else if (variant) {
      // For other variants, prefer matching variant
      if (candidateVariant.includes(variantLower) || variantLower.includes(candidateVariant)) {
        score += 3;
      }
      // Penalize 1st ed / shadowless entry when user does NOT have 1st ed
      if (candidate1stOrShadowless) {
        score -= 10;
      }
    } else {
      // No variant specified - avoid shadowless/1st ed unless set name explicitly says it
      if (candidate1stOrShadowless && !targetSet.includes('shadowless')) {
        score -= 10;
      }
    }

    if (score > 0) {
      scoredCandidates.push({ card, score });
    }
  }

  // Sort by score descending
  scoredCandidates.sort((a, b) => b.score - a.score);

  if (scoredCandidates.length > 0) {
    console.log(`[Poketrace] Scored candidates (user variant: "${variant || 'none'}"):`);
    scoredCandidates.forEach((c, i) => {
      console.log(`  [${i}] score: ${c.score} | ${c.card.set?.name} | variant: ${c.card.variant} | #${c.card.cardNumber}`);
    });
    console.log('[Poketrace] Best match:', scoredCandidates[0].card.set?.name, scoredCandidates[0].card.variant, 'score:', scoredCandidates[0].score);
    console.timeEnd(perfLabel);
    return scoredCandidates[0].card;
  }

  // Fallback: match by set name alone (exact or base match only)
  if (setName) {
    const targetSet = setName.toLowerCase();
    for (const card of searchPool) {
      const candidateSet = (card.set?.name || '').toLowerCase();
      const candidateBase = candidateSet.replace(/\s*\(.*\)/, '').trim();
      if (candidateBase === targetSet || candidateSet === targetSet) {
        console.timeEnd(perfLabel);
        return card;
      }
    }
  }

  // !! DO NOT add a fallback here that returns searchPool[0] or any unmatched card !!
  // Returning a wrong card produces wrong prices. Null is correct - tile shows "?".
  console.warn('[Poketrace] No matching card found for:', searchQuery);
  console.timeEnd(perfLabel);
  return null;
}

/**
 * Fetch full pricing data for a Poketrace card ID
 */
async function fetchPoketracePricing(poketraceId) {
  const perfLabel = `[Perf] fetchPoketracePricing: ${poketraceId}`;
  console.time(perfLabel);
  if (!API_KEY) { console.timeEnd(perfLabel); return null; }

  const res = await fetch(`${API_BASE}/cards/${poketraceId}`, {
    headers: { 'X-API-Key': API_KEY }
  });

  if (!res.ok) {
    console.error('[Poketrace] Pricing fetch failed:', res.status);
    console.timeEnd(perfLabel);
    return null;
  }

  const json = await res.json();
  console.timeEnd(perfLabel);
  return json.data || json;
}

/**
 * Fetch individual sold listings for a Poketrace card ID.
 * Paginates through all pages (cursor-based), handles rate limiting.
 * Caps at 10 pages (200 listings) to avoid excessive API calls.
 * Filters to US eBay data via ?country=us.
 *
 * Response shape:
 * {
 *   "data": [...listings],
 *   "pagination": { "hasMore": true, "nextCursor": "MjA=", "count": 20 },
 *   "rateLimit": { "remaining": 97, "max": 100 }
 * }
 */
export async function fetchPoketraceListings(poketraceId) {
  const perfLabel = `[Perf] fetchPoketraceListings: ${poketraceId}`;
  console.time(perfLabel);
  if (!API_KEY || !poketraceId) { console.timeEnd(perfLabel); return []; }

  const allListings = [];
  let cursor = null;
  let pageCount = 0;
  let rateLimitRetries = 0;
  const MAX_PAGES = 10;
  const MAX_RATE_LIMIT_RETRIES = 3;

  while (pageCount < MAX_PAGES) {
    const params = new URLSearchParams({ country: 'us' });
    if (cursor) params.set('cursor', cursor);
    const url = `${API_BASE}/cards/${poketraceId}/listings?${params.toString()}`;

    let res;
    try {
      res = await fetch(url, {
        headers: { 'X-API-Key': API_KEY }
      });
    } catch (err) {
      console.error('[Poketrace] Listings fetch network error:', err);
      break;
    }

    if (!res.ok) {
      // Handle rate limiting with retry cap
      if (res.status === 429 && rateLimitRetries < MAX_RATE_LIMIT_RETRIES) {
        rateLimitRetries++;
        const waitTime = 4;
        console.warn(`[Poketrace] Rate limited (retry ${rateLimitRetries}/${MAX_RATE_LIMIT_RETRIES}), waiting ${waitTime}s...`);
        await new Promise(resolve => setTimeout(resolve, waitTime * 1000));
        continue; // Retry same page
      }
      if (res.status === 429) {
        console.warn(`[Poketrace] Rate limit retries exhausted, returning ${allListings.length} listings collected so far`);
        break;
      }
      console.error('[Poketrace] Listings fetch failed:', res.status);
      break;
    }

    // Reset retry counter on success
    rateLimitRetries = 0;

    let json;
    try {
      json = await res.json();
    } catch {
      console.error('[Poketrace] Listings JSON parse error');
      break;
    }

    const listings = json.data || [];
    if (listings.length === 0) break;

    allListings.push(...listings);
    pageCount++;

    const pagination = json.pagination || {};
    const rateLimit = json.rateLimit || {};
    console.log(`[Poketrace] Listings page ${pageCount}: ${listings.length} items (total: ${allListings.length}, remaining: ${rateLimit.remaining ?? '?'})`);

    if (!pagination.hasMore) break;
    cursor = pagination.nextCursor;
    if (!cursor) break;

    // Brief pause between pages to be respectful of rate limits
    if (rateLimit.remaining != null && rateLimit.remaining < 5) {
      console.warn('[Poketrace] Rate limit low, pausing 3s...');
      await new Promise(resolve => setTimeout(resolve, 3000));
    }
  }

  console.log(`[Poketrace] Total listings fetched: ${allListings.length} across ${pageCount} pages`);
  console.timeEnd(perfLabel);
  return allListings;
}

// ---- Supabase Cache Layer ----

/**
 * Look up cached pricing data from Supabase by card name + number
 */
async function getCachedPrice(cardName, cardNumber) {
  const { data, error } = await supabase
    .from('poketrace_price_cache')
    .select('*')
    .eq('card_name', cardName)
    .eq('card_number', cardNumber || '')
    .maybeSingle();

  if (error) {
    console.error('[Poketrace] Cache lookup error:', error);
    return null;
  }
  return data;
}

/**
 * Look up cached pricing by poketrace_id
 */
async function getCachedPriceById(poketraceId) {
  const { data, error } = await supabase
    .from('poketrace_price_cache')
    .select('*')
    .eq('poketrace_id', poketraceId)
    .maybeSingle();

  if (error) {
    console.error('[Poketrace] Cache lookup error:', error);
    return null;
  }
  return data;
}

/**
 * Bulk fetch cached prices for multiple cards in a single query.
 * Returns a Map keyed by "cardName||cardNumber" with formatted price results.
 */
export async function getBulkCachedPrices(cards) {
  if (!cards || cards.length === 0) return new Map();

  const cardNames = [...new Set(cards.map(c => c.cardName))];

  const { data, error } = await supabase
    .from('poketrace_price_cache')
    .select('*')
    .in('card_name', cardNames);

  if (error) {
    console.error('[Poketrace] Bulk cache lookup error:', error);
    return new Map();
  }

  // Index cached rows by card_name + card_number
  const cacheMap = new Map();
  for (const row of (data || [])) {
    const key = `${row.card_name}||${row.card_number || ''}`;
    cacheMap.set(key, row);
  }

  // Build results
  const results = new Map();
  for (const card of cards) {
    const key = `${card.cardName}||${card.cardNumber || ''}`;
    const cached = cacheMap.get(key);
    if (cached) {
      const result = formatPriceResult(cached.price_data, card.grade, cached.fetched_at);
      if (result?.price) {
        results.set(card.id, result);
      }
    }
  }

  return results;
}

/**
 * Save pricing data to Supabase cache
 */
async function savePriceToCache(poketraceId, cardName, setName, cardNumber, priceData) {
  const { error } = await supabase
    .from('poketrace_price_cache')
    .upsert({
      poketrace_id: poketraceId,
      card_name: cardName,
      set_name: setName || null,
      card_number: cardNumber || null,
      price_data: priceData,
      fetched_at: new Date().toISOString(),
    }, { onConflict: 'poketrace_id' });

  if (error) {
    console.error('[Poketrace] Cache save error:', error);
  }
}

// ---- Public API ----

/**
 * Get pricing for a card. Checks Supabase cache first, then fetches from Poketrace.
 *
 * @param {Object} params
 * @param {string} params.cardName - Card name (e.g. "Charizard")
 * @param {string} [params.cardNumber] - Card number (e.g. "004/102")
 * @param {string} [params.setName] - Set name (e.g. "Base Set")
 * @param {string} [params.grade] - User's grade/condition (e.g. "PSA 10", "NM")
 * @param {boolean} [params.forceRefresh] - Skip cache, fetch fresh from API
 * @returns {Object|null} Price data or null
 */
export async function getCardPrice({ cardName, cardNumber, setName, grade, forceRefresh = false }) {
  if (!cardName) return null;
  console.log('[Poketrace] getCardPrice called:', { cardName, cardNumber, setName, grade });

  try {
    // 1. Check Supabase cache (unless force refresh)
    if (!forceRefresh) {
      const cached = await getCachedPrice(cardName, cardNumber);
      if (cached) {
        console.log('[Poketrace] Cache hit for', cardName, '- has prices:', !!cached.price_data?.prices);
        return formatPriceResult(cached.price_data, grade, cached.fetched_at);
      }
    }

    // 2. Search Poketrace for this card
    const searchResult = await searchPoketraceCard(cardName, cardNumber, setName);
    console.log('[Poketrace] Search result for', cardName, ':', searchResult ? searchResult.id : 'null');
    if (!searchResult) return null;

    const poketraceId = searchResult.id;

    // 3. Check cache by poketrace_id (in case name/number didn't match but ID exists)
    if (!forceRefresh) {
      const cachedById = await getCachedPriceById(poketraceId);
      if (cachedById) {
        console.log('[Poketrace] Cache hit by ID for', cardName);
        return formatPriceResult(cachedById.price_data, grade, cachedById.fetched_at);
      }
    }

    // 4. Fetch full pricing from Poketrace
    const priceData = await fetchPoketracePricing(poketraceId);
    console.log('[Poketrace] Pricing data for', cardName, ':', priceData ? { hasPrices: !!priceData.prices, keys: Object.keys(priceData.prices || {}) } : 'null');
    if (!priceData) return null;

    // 5. Save to Supabase cache
    await savePriceToCache(
      poketraceId,
      cardName,
      priceData.set?.name || setName,
      priceData.cardNumber || cardNumber,
      priceData
    );

    return formatPriceResult(priceData, grade, new Date().toISOString());
  } catch (err) {
    console.error('[Poketrace] getCardPrice error:', err);

    // On network error, try to return stale cache
    const cached = await getCachedPrice(cardName, cardNumber);
    if (cached) {
      return formatPriceResult(cached.price_data, grade, cached.fetched_at);
    }
    return null;
  }
}

/**
 * Get all pricing tiers for a card (for the graded price modal).
 * Returns the full breakdown of graded + raw prices.
 *
 * @param {Object} params
 * @param {string} params.cardName
 * @param {string} [params.cardNumber]
 * @param {string} [params.setName]
 * @param {string} [params.source] - "ebay" or "tcgplayer" (default: "ebay")
 * @param {boolean} [params.forceRefresh]
 * @returns {Object|null}
 */
export async function getAllPriceTiers({ cardName, cardNumber, setName, source = 'ebay', forceRefresh = false }) {
  if (!cardName) return null;

  try {
    let priceData = null;

    if (!forceRefresh) {
      const cached = await getCachedPrice(cardName, cardNumber);
      if (cached) {
        // Unwrap if cached with old { data: { ... } } format
        priceData = cached.price_data?.data || cached.price_data;
      }
    }

    if (!priceData) {
      const searchResult = await searchPoketraceCard(cardName, cardNumber, setName);
      if (!searchResult) return null;

      priceData = await fetchPoketracePricing(searchResult.id);
      if (!priceData) return null;

      await savePriceToCache(
        searchResult.id,
        cardName,
        priceData.set?.name || setName,
        priceData.cardNumber || cardNumber,
        priceData
      );
    }

    const prices = priceData.prices?.[source] || {};

    // Split into graded and raw
    const graded = {};
    const raw = {};

    for (const [tier, data] of Object.entries(prices)) {
      if (RAW_TIERS.includes(tier)) {
        raw[tier] = data;
      } else {
        graded[tier] = data;
      }
    }

    // Sort graded tiers
    const sortedGraded = Object.entries(graded)
      .sort(([a], [b]) => {
        const ai = GRADE_SORT.indexOf(a);
        const bi = GRADE_SORT.indexOf(b);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      })
      .reduce((acc, [k, v]) => { acc[k] = v; return acc; }, {});

    // Sort raw tiers
    const sortedRaw = Object.entries(raw)
      .sort(([a], [b]) => RAW_TIERS.indexOf(a) - RAW_TIERS.indexOf(b))
      .reduce((acc, [k, v]) => { acc[k] = v; return acc; }, {});

    return {
      cardName: priceData.name || cardName,
      setName: priceData.set?.name || setName,
      cardNumber: priceData.cardNumber || cardNumber,
      image: priceData.image,
      market: priceData.market,
      currency: priceData.currency || 'USD',
      topPrice: priceData.topPrice,
      totalSales: priceData.totalSaleCount,
      source,
      graded: sortedGraded,
      raw: sortedRaw,
    };
  } catch (err) {
    console.error('[Poketrace] getAllPriceTiers error:', err);
    return null;
  }
}

/**
 * Format the price result for a specific grade/condition
 */
function formatPriceResult(priceData, grade, fetchedAt) {
  // Unwrap if cached with old { data: { ... } } format
  const d = priceData?.data || priceData;
  const poketraceKey = gradeToPoketraceKey(grade);
  const graded = grade ? isGradedCard(grade) : false;

  // Default source: ebay, fallback to cardmarket for EU cards
  const ebayPrices = d.prices?.ebay || {};
  const tcgPrices = d.prices?.tcgplayer || {};
  const cmPrices = d.prices?.cardmarket || {};

  let tierData = null;
  let usedFallback = false;

  if (poketraceKey) {
    // Try exact match in eBay data first
    tierData = ebayPrices[poketraceKey];

    // Try TCGPlayer if eBay doesn't have it (raw conditions only)
    if (!tierData && !graded) {
      tierData = tcgPrices[poketraceKey];
    }

    // Try CardMarket (EU cards) - use UNKNOWN tier if specific tier missing
    if (!tierData) {
      tierData = cmPrices[poketraceKey] || cmPrices['UNKNOWN'];
    }

    // Fallback: use NEAR_MINT with multiplier for raw conditions
    if (!tierData && !graded) {
      const nmData = ebayPrices['NEAR_MINT'] || tcgPrices['NEAR_MINT'] || cmPrices['NEAR_MINT'] || cmPrices['UNKNOWN'];
      if (nmData) {
        const multiplier = CONDITION_MULTIPLIERS[poketraceKey] || 0.5;
        tierData = {
          avg: Math.round(nmData.avg * multiplier * 100) / 100,
          low: nmData.low ? Math.round(nmData.low * multiplier * 100) / 100 : null,
          high: nmData.high ? Math.round(nmData.high * multiplier * 100) / 100 : null,
          saleCount: 0,
          trend: nmData.trend,
          confidence: 'estimated',
        };
        usedFallback = true;
      }
    }
  }

  return {
    cardName: d.name,
    setName: d.set?.name,
    cardNumber: d.cardNumber,
    image: d.image,
    market: d.market,
    currency: d.currency || 'USD',
    topPrice: d.topPrice,
    grade,
    poketraceKey,
    // Flatten for easy consumption
    price: tierData?.avg || null,
    low: tierData?.low || null,
    high: tierData?.high || null,
    saleCount: tierData?.saleCount || 0,
    trend: tierData?.trend || null,
    confidence: tierData?.confidence || null,
    median3d: tierData?.median3d || null,
    median7d: tierData?.median7d || null,
    median30d: tierData?.median30d || null,
    usedFallback,
    fetchedAt,
    // Keep full data for modal drill-down
    allPrices: d.prices,
  };
}

/**
 * Format a Poketrace tier key to a human-readable label
 * e.g. "PSA_10" -> { company: "PSA", grade: "10" }
 * e.g. "CGC_9_5" -> { company: "CGC", grade: "9.5" }
 * e.g. "NEAR_MINT" -> { company: null, grade: "Near Mint" }
 */
export function formatTierLabel(tier) {
  if (RAW_TIERS.includes(tier)) {
    return {
      company: null,
      grade: tier.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()),
    };
  }

  const parts = tier.split('_');
  const company = parts[0];
  const grade = parts.slice(1).join('.');
  return { company, grade };
}

/**
 * Format a price as USD currency string
 */
export function formatPrice(price) {
  if (price === null || price === undefined) return null;
  if (price < 1) return `$${price.toFixed(2)}`;
  if (price < 10) return `$${price.toFixed(2)}`;
  if (price < 100) return `$${price.toFixed(0)}`;
  if (price < 1000) return `$${price.toFixed(0)}`;
  return `$${(price / 1000).toFixed(1)}k`;
}

/**
 * Format price for detail view (always show cents)
 */
export function formatPriceDetail(price) {
  if (price === null || price === undefined) return '--';
  return `$${price.toFixed(2)}`;
}

/**
 * Get full Poketrace pricing for Chef's Lab.
 * Returns the complete pricing object with all graded + raw tiers.
 * Checks poketrace_price_cache first, then fetches from API.
 */
export async function getFullCardPricing({ cardName, cardNumber, setName, variant }) {
  const perfLabel = `[Perf] getFullCardPricing: ${cardName}`;
  console.time(perfLabel);
  if (!cardName) { console.timeEnd(perfLabel); return null; }

  try {
    // Check cache first
    const cached = await getCachedPrice(cardName, cardNumber);
    if (cached) {
      const d = cached.price_data?.data || cached.price_data;
      if (d?.prices) {
        console.log('[Perf] getFullCardPricing: CACHE HIT (poketrace_price_cache)');
        console.timeEnd(perfLabel);
        return { priceData: d, poketraceId: cached.poketrace_id || null };
      }
    }

    // Search Poketrace (with variant for 1st Edition / Shadowless matching)
    const searchResult = await searchPoketraceCard(cardName, cardNumber, setName, variant);
    if (!searchResult) return null;

    const poketraceId = searchResult.id;

    // Check cache by ID
    const cachedById = await getCachedPriceById(poketraceId);
    if (cachedById) {
      const d = cachedById.price_data?.data || cachedById.price_data;
      if (d?.prices) {
        console.log('[Perf] getFullCardPricing: CACHE HIT (by poketraceId)');
        console.timeEnd(perfLabel);
        return { priceData: d, poketraceId };
      }
    }

    // Fetch fresh
    const priceData = await fetchPoketracePricing(poketraceId);
    if (!priceData) return null;

    await savePriceToCache(
      poketraceId,
      cardName,
      priceData.set?.name || setName,
      priceData.cardNumber || cardNumber,
      priceData
    );

    console.timeEnd(perfLabel);
    return { priceData, poketraceId };
  } catch (err) {
    console.error('[Poketrace] getFullCardPricing error:', err);
    console.timeEnd(perfLabel);
    return null;
  }
}

// Map raw condition strings from listings API to our short keys
const LISTING_CONDITION_MAP = {
  'near_mint': 'nm',
  'near mint': 'nm',
  'nm': 'nm',
  'lightly_played': 'lp',
  'lightly played': 'lp',
  'lp': 'lp',
  'moderately_played': 'mp',
  'moderately played': 'mp',
  'mp': 'mp',
  'heavily_played': 'hp',
  'heavily played': 'hp',
  'hp': 'hp',
  'damaged': 'dmg',
  'dmg': 'dmg',
};

function conditionToKey(condition) {
  if (!condition) return 'nm';
  return LISTING_CONDITION_MAP[condition.toLowerCase()] || 'nm';
}

/**
 * Transform Poketrace pricing data into Chef's Lab format.
 * If individual listings are provided, evidence comes from those instead of aggregate tiers.
 * graded_data and raw_data are always built from aggregate tier data for quick lookup.
 *
 * @param {Object} poketraceData - Aggregate pricing data from Poketrace
 * @param {Array} [listings] - Individual sold listings from /listings endpoint
 * @returns {Object} { graded_data, raw_data, evidence, card_info }
 */
export function transformForChefsLab(poketraceData, listings) {
  if (!poketraceData?.prices) return null;

  const ebay = poketraceData.prices.ebay || {};
  const tcg = poketraceData.prices.tcgplayer || {};
  const cm = poketraceData.prices.cardmarket || {};

  const graded_data = {};
  const raw_data = {};

  // Raw condition mapping
  const RAW_MAP = {
    'NEAR_MINT': 'nm',
    'LIGHTLY_PLAYED': 'lp',
    'MODERATELY_PLAYED': 'mp',
    'HEAVILY_PLAYED': 'hp',
    'DAMAGED': 'dmg',
  };

  // Always build graded_data and raw_data from aggregate tiers
  for (const [tier, data] of Object.entries(ebay)) {
    if (RAW_MAP[tier]) {
      raw_data[RAW_MAP[tier]] = data.avg;
    } else {
      const parts = tier.split('_');
      const company = parts[0].toLowerCase();
      const gradeNum = parts.slice(1).join('.');
      if (!graded_data[company]) graded_data[company] = {};
      graded_data[company][gradeNum] = data.avg;
    }
  }

  // Fill in TCGPlayer raw prices as fallback
  for (const [tier, data] of Object.entries(tcg)) {
    if (RAW_MAP[tier] && !raw_data[RAW_MAP[tier]]) {
      raw_data[RAW_MAP[tier]] = data.avg;
    }
  }

  // Fill in CardMarket as last fallback
  for (const [tier, data] of Object.entries(cm)) {
    if (RAW_MAP[tier] && !raw_data[RAW_MAP[tier]]) {
      raw_data[RAW_MAP[tier]] = data.avg;
    }
  }

  // Build evidence: individual listings + aggregate fallback for grades not covered
  let evidence = [];

  if (listings && listings.length > 0) {
    // Start with individual listings
    evidence = listings.map(listing => ({
      company: listing.grader?.toLowerCase() || 'raw',
      grade_number: listing.grade
        ? (parseFloat(listing.grade) || listing.grade)
        : conditionToKey(listing.condition),
      price: listing.price,
      title: listing.title,
      url: listing.listingUrl,
      date: listing.soldAt ? listing.soldAt.split('T')[0] : null,
      source: 'ebay',
      anomalyFlag: listing.anomalyFlag || false,
      anomalyReason: listing.anomalyReason || null,
      listingType: listing.listingType,
      condition: listing.condition,
      currency: listing.currency,
    }));

    // Track which company+grade combos are covered by individual listings
    const coveredKeys = new Set();
    for (const e of evidence) {
      coveredKeys.add(`${e.company}_${e.grade_number}`);
    }

    // Fill in aggregate tiers for grades NOT covered by individual listings
    for (const [tier, data] of Object.entries(ebay)) {
      if (RAW_MAP[tier]) {
        const key = `raw_${RAW_MAP[tier]}`;
        if (!coveredKeys.has(key)) {
          evidence.push({
            company: 'raw',
            grade_number: RAW_MAP[tier],
            price: data.avg,
            low: data.low,
            high: data.high,
            date: data.lastUpdated ? data.lastUpdated.split('T')[0] : null,
            saleCount: data.saleCount || 0,
            trend: data.trend,
            confidence: data.confidence,
            median3d: data.median3d,
            median7d: data.median7d,
            median30d: data.median30d,
            source: 'ebay',
            isAggregate: true,
          });
        }
      } else {
        const parts = tier.split('_');
        const company = parts[0].toLowerCase();
        const gradeNum = parseFloat(parts.slice(1).join('.')) || parts.slice(1).join('.');
        const key = `${company}_${gradeNum}`;
        if (!coveredKeys.has(key)) {
          evidence.push({
            company,
            grade_number: gradeNum,
            price: data.avg,
            low: data.low,
            high: data.high,
            date: data.lastUpdated ? data.lastUpdated.split('T')[0] : null,
            saleCount: data.saleCount || 0,
            trend: data.trend,
            confidence: data.confidence,
            median3d: data.median3d,
            median7d: data.median7d,
            median30d: data.median30d,
            source: 'ebay',
            isAggregate: true,
          });
        }
      }
    }
  } else {
    // No individual listings: build evidence entirely from aggregate tiers
    for (const [tier, data] of Object.entries(ebay)) {
      if (RAW_MAP[tier]) {
        evidence.push({
          company: 'raw',
          grade_number: RAW_MAP[tier],
          price: data.avg,
          low: data.low,
          high: data.high,
          date: data.lastUpdated ? data.lastUpdated.split('T')[0] : null,
          saleCount: data.saleCount || 0,
          trend: data.trend,
          confidence: data.confidence,
          median3d: data.median3d,
          median7d: data.median7d,
          median30d: data.median30d,
          source: 'ebay',
        });
      } else {
        const parts = tier.split('_');
        const company = parts[0].toLowerCase();
        const gradeNum = parts.slice(1).join('.');
        evidence.push({
          company,
          grade_number: parseFloat(gradeNum) || gradeNum,
          price: data.avg,
          low: data.low,
          high: data.high,
          date: data.lastUpdated ? data.lastUpdated.split('T')[0] : null,
          saleCount: data.saleCount || 0,
          trend: data.trend,
          confidence: data.confidence,
          median3d: data.median3d,
          median7d: data.median7d,
          median30d: data.median30d,
          source: 'ebay',
        });
      }
    }
  }

  return {
    graded_data,
    raw_data,
    evidence,
    card_info: {
      name: poketraceData.name,
      set: poketraceData.set?.name,
      number: poketraceData.cardNumber,
      image: poketraceData.image,
      topPrice: poketraceData.topPrice,
      totalSaleCount: poketraceData.totalSaleCount,
      hasGraded: poketraceData.hasGraded,
      gradedOptions: poketraceData.gradedOptions,
      conditionOptions: poketraceData.conditionOptions,
    },
  };
}

// ---- Multi-Source Pricing ----

const GEMINI_API_KEY = 'AIzaSyAfcHjN75Fp1VKC1vUe2Jp2kUGynxZxQQk';

/**
 * Extract per-condition raw data from full Poketrace pricing.
 * Returns { raw, evidence, meta } with detailed breakdown.
 */
function extractPoketraceRaw(poketraceData) {
  if (!poketraceData?.prices) return null;

  const ebay = poketraceData.prices.ebay || {};
  const tcg = poketraceData.prices.tcgplayer || {};
  const cm = poketraceData.prices.cardmarket || {};

  const RAW_MAP = {
    'NEAR_MINT': 'nm',
    'LIGHTLY_PLAYED': 'lp',
    'MODERATELY_PLAYED': 'mp',
    'HEAVILY_PLAYED': 'hp',
    'DAMAGED': 'dmg',
  };

  const raw = {};
  const evidence = [];

  for (const [tier, data] of Object.entries(ebay)) {
    if (RAW_MAP[tier]) {
      const key = RAW_MAP[tier];
      raw[key] = {
        avg: data.avg || null,
        low: data.low || null,
        high: data.high || null,
        saleCount: data.saleCount || 0,
        trend: data.trend || null,
        confidence: data.confidence || null,
        median3d: data.median3d || null,
        median7d: data.median7d || null,
        median30d: data.median30d || null,
        source: 'ebay',
      };
      evidence.push({
        company: 'raw',
        grade_number: key,
        price: data.avg,
        low: data.low,
        high: data.high,
        saleCount: data.saleCount || 0,
        trend: data.trend,
        source: 'ebay',
      });
    }
  }

  // Fill from TCGPlayer as fallback
  for (const [tier, data] of Object.entries(tcg)) {
    const key = RAW_MAP[tier];
    if (key && !raw[key]) {
      raw[key] = {
        avg: data.avg || null,
        low: data.low || null,
        high: data.high || null,
        saleCount: data.saleCount || 0,
        trend: data.trend || null,
        confidence: data.confidence || null,
        source: 'tcgplayer',
      };
    }
  }

  // Fill from CardMarket as last fallback
  for (const [tier, data] of Object.entries(cm)) {
    const key = RAW_MAP[tier];
    if (key && !raw[key]) {
      raw[key] = {
        avg: data.avg || null,
        low: data.low || null,
        high: data.high || null,
        saleCount: data.saleCount || 0,
        source: 'cardmarket',
      };
    }
  }

  return {
    raw,
    evidence,
    meta: {
      totalSaleCount: poketraceData.totalSaleCount || 0,
      topPrice: poketraceData.topPrice || null,
      currency: poketraceData.currency || 'USD',
      market: poketraceData.market || null,
    },
  };
}

/**
 * Call Gemini to analyze pricing from multiple sources.
 */
async function callGeminiPricingAnalysis({ cardName, cardNumber, setName, variant, poketrace, tcgdex, justTCG, listings }) {
  const perfLabel = `[Perf] callGeminiPricingAnalysis: ${cardName}`;
  console.time(perfLabel);
  try {
    const pt = poketrace?.raw || {};
    const tcg = tcgdex?.tcgplayer || {};
    const cm = tcgdex?.cardmarket || {};

    const fmtCond = (key) => {
      const d = pt[key];
      if (!d?.avg) return 'N/A';
      return `$${d.avg.toFixed(2)} avg (${d.saleCount || 0} sales${d.low ? `, range $${d.low.toFixed(2)}-$${d.high?.toFixed(2) || '?'}` : ''})`;
    };

    const tcgVariant = tcg.holofoil || tcg.normal || tcg.reverseHolofoil || {};
    const fmtTcg = (field) => tcgVariant[field] ? `$${tcgVariant[field].toFixed(2)}` : 'N/A';

    // Build JustTCG section for prompt
    let justTCGSection = '';
    if (justTCG?.conditions) {
      const jc = justTCG.conditions;
      const fmtJ = (key) => {
        const d = jc[key];
        if (!d?.price) return 'N/A';
        let s = `$${d.price.toFixed(2)}`;
        if (d.change7d != null) s += ` (7d: ${d.change7d >= 0 ? '+' : ''}${d.change7d.toFixed(1)}%)`;
        return s;
      };
      justTCGSection = `\nSOURCE 3 - JustTCG (TCGPlayer marketplace by condition${justTCG.variant ? `, ${justTCG.variant}` : ''}):
  NM: ${fmtJ('nm')}
  LP: ${fmtJ('lp')}
  MP: ${fmtJ('mp')}
  HP: ${fmtJ('hp')}
  DMG: ${fmtJ('dmg')}`;
      const nmData = jc.nm;
      if (nmData?.change30d != null) {
        justTCGSection += `\n  30d trend: ${nmData.change30d >= 0 ? '+' : ''}${nmData.change30d.toFixed(1)}% (NM)`;
      }
      justTCGSection += '\n';
    }

    // Build recent sold listings section from individual listings
    let recentSalesSection = '';
    if (listings && listings.length > 0) {
      const sortedListings = [...listings]
        .filter(l => l.price && l.soldAt)
        .sort((a, b) => new Date(b.soldAt) - new Date(a.soldAt));

      // Group by grader+grade or condition, show up to 5 recent per group
      const groups = {};
      for (const l of sortedListings) {
        const key = l.grader
          ? `${l.grader} ${l.grade}`
          : (l.condition || 'Unknown');
        if (!groups[key]) groups[key] = [];
        if (groups[key].length < 5) {
          const dateStr = l.soldAt.split('T')[0];
          groups[key].push(`$${l.price.toFixed(2)} (${dateStr})`);
        }
      }

      const lines = Object.entries(groups)
        .map(([key, sales]) => `  ${key}: ${sales.join(', ')}`)
        .join('\n');

      recentSalesSection = `\nRECENT INDIVIDUAL SOLD LISTINGS (${listings.length} total):
${lines}\n`;
    }

    const prompt = `You are a Pokemon TCG pricing analyst and card market expert. Given market data from multiple sources, evaluate data quality, estimate fair raw condition price RANGES, and provide rich card market intelligence.

CARD: ${cardName} #${cardNumber || '?'} from ${setName || 'Unknown'} (${variant || 'Normal'})

SOURCE 1 - Poketrace (eBay/TCGPlayer sold data by condition):
  NM: ${fmtCond('nm')}
  LP: ${fmtCond('lp')}
  MP: ${fmtCond('mp')}
  HP: ${fmtCond('hp')}
  DMG: ${fmtCond('dmg')}

SOURCE 2 - TCGdex (market listings, ~NM equivalent):
  TCGPlayer Market: ${fmtTcg('market')}
  TCGPlayer Low: ${fmtTcg('low')}
  TCGPlayer High: ${fmtTcg('high')}
  CardMarket Avg: ${cm.avg ? `EUR ${cm.avg.toFixed(2)} (~USD ${cm.avgUSD?.toFixed(2) || '?'})` : 'N/A'}
  CardMarket 30d Avg: ${cm.avg30 ? `EUR ${cm.avg30.toFixed(2)}` : 'N/A'}
${justTCGSection}${recentSalesSection}
TASKS:

0. DATA QUALITY CHECK - Before pricing, evaluate the source data:
   - Do these listings match the actual card? Check name/set/number alignment
   - Are there enough sales for reliable pricing?
   - Do the sources agree, or is there suspicious discrepancy?
   - IMPORTANT: JustTCG (SOURCE 3) provides VARIANT-SPECIFIC pricing (e.g., "1st Edition Holofoil"). TCGdex (SOURCE 2) provides GENERIC market averages that may reflect a DIFFERENT variant or region (e.g., Unlimited, European market). If JustTCG has per-condition data for the correct variant, treat the data as "good" even if TCGdex/CardMarket prices differ -- that discrepancy is expected (variant/region mismatch, not bad data).
   - Return data_quality: "good", "suspicious", or "insufficient"
   - If not good, explain why in data_quality_reasoning

1. PRICE ESTIMATES - For each condition (NM, LP, MP, HP, DMG):
   - Provide a realistic USD price RANGE (low and high)
   - Be confident, not conservative -- reflect actual market spread
   - The high end should represent what a seller can reasonably expect
   - The low end should represent a fair buy price
   - Weight JustTCG per-condition prices most heavily when available (variant-specific TCGPlayer data). Then Poketrace. For missing conditions use standard multipliers (LP ~80%, MP ~60%, HP ~40%, DMG ~25% of NM)
   - One-sentence reasoning

2. MARKET INTELLIGENCE SUMMARY - Write a 4-6 sentence market brief covering ALL of these:
   - Card Identity: What this card is and why it matters (chase card? vintage staple? competitive use? iconic artwork?)
   - Demand Drivers: What drives demand - nostalgia, set popularity, grading pop reports, variant scarcity, competitive play
   - Market Trend: Is this card trending up, down, or stable? Any recent price spikes or drops?
   - Pricing Analysis: How the two sources compare and what the spread tells us
   - Trade Perspective: Brief take on whether this is a good hold, buy, or sell right now

Return JSON:
{
  "data_quality": "good",
  "data_quality_reasoning": "Both sources agree and have sufficient sales data",
  "conditions": {
    "nm": { "low": 40.00, "high": 50.00, "reasoning": "..." },
    "lp": { "low": 32.00, "high": 40.00, "reasoning": "..." },
    "mp": { "low": 24.00, "high": 30.00, "reasoning": "..." },
    "hp": { "low": 16.00, "high": 20.00, "reasoning": "..." },
    "dmg": { "low": 10.00, "high": 12.50, "reasoning": "..." }
  },
  "summary": "4-6 sentence market brief covering card identity, demand drivers, pricing analysis, market trend, and trade perspective.",
  "confidence": "high"
}`;

    console.log('[MultiSource] Calling Gemini pricing analysis...');

    const res = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: 0.2,
            maxOutputTokens: 8192,
            responseMimeType: 'application/json',
          },
        }),
      }
    );

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      console.error('[MultiSource] Gemini API error:', res.status, errText);
      console.timeEnd(perfLabel);
      return null;
    }

    const data = await res.json();
    console.log('[MultiSource] Gemini response candidates:', data.candidates?.length || 0);

    if (!data.candidates || data.candidates.length === 0) {
      console.error('[MultiSource] Gemini returned no candidates:', JSON.stringify(data).slice(0, 500));
      console.timeEnd(perfLabel);
      return null;
    }

    const parts = data.candidates[0]?.content?.parts || [];
    const textPart = parts.find(p => !p.thought && p.text) || parts[0];

    if (!textPart?.text) {
      console.error('[MultiSource] Gemini no text in response parts:', parts.length);
      console.timeEnd(perfLabel);
      return null;
    }

    console.log('[MultiSource] Gemini raw text:', textPart.text.slice(0, 200));

    // Parse JSON - strip markdown fences if present
    let jsonStr = textPart.text.trim();
    jsonStr = jsonStr.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const parsed = JSON.parse(jsonStr);

    console.log('[MultiSource] Data quality:', parsed.data_quality, parsed.data_quality_reasoning || '');

    console.timeEnd(perfLabel);
    return {
      raw: parsed.conditions || {},
      data_quality: parsed.data_quality || 'good',
      data_quality_reasoning: parsed.data_quality_reasoning || '',
      summary: parsed.summary || '',
      confidence: parsed.confidence || 'medium',
      model: 'gemini-2.5-flash',
      usedGrounding: false,
      fetchedAt: new Date().toISOString(),
    };
  } catch (err) {
    console.error('[MultiSource] Gemini pricing analysis error:', err.message || err);
    console.timeEnd(perfLabel);
    return null;
  }
}

/**
 * Call Gemini WITH Google Search grounding when data quality is bad.
 * Uses tools: [{ google_search: {} }] which is incompatible with responseMimeType.
 * Extracts JSON from text response manually.
 */
async function callGeminiWithGrounding({ cardName, cardNumber, setName, variant, dataQualityReason }) {
  const perfLabel = `[Perf] callGeminiWithGrounding: ${cardName}`;
  console.time(perfLabel);
  try {
    const prompt = `You are a Pokemon TCG pricing analyst. The automated data sources provided UNRELIABLE data for this card.
Reason: ${dataQualityReason}

CARD: ${cardName} #${cardNumber || '?'} from ${setName || 'Unknown'} (${variant || 'Normal'})

Use Google Search to find current market data from:
- PriceCharting.com, eBay sold listings, PSA auction prices, TCGPlayer

Explain what was wrong with the original data and what you found instead.

For each condition (NM, LP, MP, HP, DMG), provide a realistic USD price RANGE (low and high).
Be confident -- reflect actual market reality. The high end should represent what a seller can expect.

Return JSON (and ONLY JSON, no other text):
{
  "data_quality": "corrected",
  "data_quality_reasoning": "Explain what was wrong and what you found",
  "conditions": {
    "nm": { "low": 1800, "high": 2200, "reasoning": "..." },
    "lp": { "low": 1200, "high": 1500, "reasoning": "..." },
    "mp": { "low": 800, "high": 1000, "reasoning": "..." },
    "hp": { "low": 500, "high": 700, "reasoning": "..." },
    "dmg": { "low": 300, "high": 450, "reasoning": "..." }
  },
  "summary": "Market brief with what you found from web search",
  "confidence": "high"
}`;

    console.log('[MultiSource] Calling Gemini with grounding...');

    const res = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }],
          tools: [{ google_search: {} }],
          generationConfig: {
            temperature: 0.2,
            maxOutputTokens: 8192,
          },
        }),
      }
    );

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      console.error('[MultiSource] Gemini grounding API error:', res.status, errText);
      console.timeEnd(perfLabel);
      return null;
    }

    const data = await res.json();

    if (!data.candidates || data.candidates.length === 0) {
      console.error('[MultiSource] Gemini grounding returned no candidates');
      console.timeEnd(perfLabel);
      return null;
    }

    const parts = data.candidates[0]?.content?.parts || [];
    const textPart = parts.find(p => !p.thought && p.text) || parts[0];

    if (!textPart?.text) {
      console.error('[MultiSource] Gemini grounding no text in response');
      console.timeEnd(perfLabel);
      return null;
    }

    console.log('[MultiSource] Gemini grounding raw text:', textPart.text.slice(0, 300));

    // Extract JSON from text (no responseMimeType with tools)
    let jsonStr = textPart.text.trim();
    jsonStr = jsonStr.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.error('[MultiSource] Gemini grounding: no JSON found in response');
      console.timeEnd(perfLabel);
      return null;
    }

    const parsed = JSON.parse(jsonMatch[0]);

    // Extract grounding metadata if available
    const groundingMeta = data.candidates[0]?.groundingMetadata || {};
    const searchQueries = groundingMeta.webSearchQueries || [];
    const groundingSources = groundingMeta.groundingChunks?.map(c => ({
      url: c.web?.uri,
      title: c.web?.title,
    })).filter(s => s.url) || [];

    console.log('[MultiSource] Grounding sources:', groundingSources.length, 'queries:', searchQueries);

    console.timeEnd(perfLabel);
    return {
      raw: parsed.conditions || {},
      data_quality: parsed.data_quality || 'corrected',
      data_quality_reasoning: parsed.data_quality_reasoning || dataQualityReason,
      groundingReason: dataQualityReason,
      summary: parsed.summary || '',
      confidence: parsed.confidence || 'medium',
      model: 'gemini-2.5-flash',
      usedGrounding: true,
      groundingMetadata: {
        searchQueries,
        sources: groundingSources,
      },
      fetchedAt: new Date().toISOString(),
    };
  } catch (err) {
    console.error('[MultiSource] Gemini grounding error:', err.message || err);
    console.timeEnd(perfLabel);
    return null;
  }
}

/**
 * Compute the "best" single price from all sources.
 * Priority: Gemini (AI analysis of all data) > Poketrace > TCGdex.
 */
function computeBestPrice(poketrace, tcgdex, gemini) {
  // Gemini-only. No fallback. If Gemini fails, return null (tile shows "?").
  const gmNm = gemini?.raw?.nm;
  if (gmNm && (gmNm.high || gmNm.price)) {
    const high = gmNm.high || gmNm.price;
    const low = gmNm.low || gmNm.price;
    return {
      price: high,
      priceLow: low,
      priceHigh: high,
      source: 'gemini',
      condition: 'NM',
      reasoning: gmNm.reasoning || 'AI-analyzed price from all sources',
      isRange: !!(gmNm.low && gmNm.high),
    };
  }

  return null;
}

/**
 * Gemini-first pricing pipeline for Fair Trade tiles.
 * Fast path: Poketrace first (need tcgplayerId), then JustTCG + TCGdex in parallel, then Gemini.
 * If data quality is bad, calls Gemini again with Google Search grounding.
 * Returns { bestPrice, gemini, poketrace, tcgdex, justTCG, poketraceId }
 */
export async function getGeminiPrimaryPrice({ cardName, cardNumber, setName, tcgdexCardId, variant }) {
  const perfLabel = `[Perf] getGeminiPrimaryPrice: ${cardName}`;
  console.time(perfLabel);
  console.log('[GeminiPrimary] Starting for:', cardName);

  // 1. Poketrace first (we need tcgplayerId from its response for JustTCG)
  let poketraceResult = null;
  try {
    poketraceResult = await getFullCardPricing({ cardName, cardNumber, setName, variant });
  } catch (err) {
    console.error('[GeminiPrimary] Poketrace error:', err);
  }

  const poketraceRaw = poketraceResult?.priceData || null;
  const poketraceId = poketraceResult?.poketraceId || null;
  const tcgplayerId = poketraceRaw?.refs?.tcgplayerId || poketraceRaw?.tcgplayerId || null;

  // 2. Parallel: TCGdex + JustTCG (JustTCG needs tcgplayerId from step 1)
  const [tcgdexSettled, justTCGSettled] = await Promise.allSettled([
    tcgdexCardId ? fetchTCGdexPricing(tcgdexCardId) : Promise.resolve(null),
    tcgplayerId ? fetchJustTCGPricing(tcgplayerId, variant) : Promise.resolve(null),
  ]);

  const tcgdex = tcgdexSettled.status === 'fulfilled' ? tcgdexSettled.value : null;
  const justTCG = justTCGSettled.status === 'fulfilled' ? justTCGSettled.value : null;

  // 3. Transform Poketrace into per-condition breakdown
  const poketrace = extractPoketraceRaw(poketraceRaw);

  console.log('[GeminiPrimary] Poketrace:', poketrace ? Object.keys(poketrace.raw).length + ' conditions' : 'null');
  console.log('[GeminiPrimary] TCGdex:', tcgdex ? 'available' : 'null');
  console.log('[GeminiPrimary] JustTCG:', justTCG ? Object.keys(justTCG.conditions).length + ' conditions' : 'null (tcgplayerId: ' + tcgplayerId + ')');

  // 4. Gemini call #1 (no listings -- fast)
  let gemini = await callGeminiPricingAnalysis({
    cardName, cardNumber, setName, variant,
    poketrace, tcgdex, justTCG, listings: null,
  });

  // 5. If data quality is bad, call Gemini with grounding
  if (gemini && gemini.data_quality !== 'good') {
    console.log('[GeminiPrimary] Data quality:', gemini.data_quality, '- calling with grounding...');
    const grounded = await callGeminiWithGrounding({
      cardName, cardNumber, setName, variant,
      dataQualityReason: gemini.data_quality_reasoning || 'Data quality flagged as ' + gemini.data_quality,
    });
    if (grounded) {
      gemini = grounded;
    }
    // If grounding fails, keep the first result
  }

  console.log('[GeminiPrimary] Gemini:', gemini ? `confidence: ${gemini.confidence}, grounded: ${gemini.usedGrounding}` : 'null');

  // 6. Compute best price
  const bestPrice = computeBestPrice(poketrace, tcgdex, gemini);

  console.timeEnd(perfLabel);
  return {
    bestPrice,
    gemini,
    poketrace,
    tcgdex,
    justTCG,
    poketraceId,
  };
}

/**
 * Fetch pricing from all sources for Chef's Lab multi-source raw tab.
 * Poketrace first (need tcgplayerId), then JustTCG + TCGdex + listings in parallel, then Gemini.
 *
 * @param {Object} params
 * @param {string} params.cardName
 * @param {string} [params.cardNumber]
 * @param {string} [params.setName]
 * @param {string} [params.tcgdexCardId] - e.g., "base1-4"
 * @param {string} [params.variant] - e.g., "Holo", "Normal"
 * @returns {Object} Multi-source result { poketrace, tcgdex, justTCG, gemini, bestPrice, fetchedAt }
 */
export async function getMultiSourcePricing({ cardName, cardNumber, setName, tcgdexCardId, variant }) {
  const perfLabel = `[Perf] getMultiSourcePricing: ${cardName}`;
  console.time(perfLabel);
  console.log('[MultiSource] Fetching for:', cardName, { cardNumber, setName, tcgdexCardId, variant });

  // 1. Poketrace first (need tcgplayerId for JustTCG)
  let poketraceResult = null;
  try {
    poketraceResult = await getFullCardPricing({ cardName, cardNumber, setName, variant });
  } catch (err) {
    console.error('[MultiSource] Poketrace error:', err);
  }

  const poketraceRaw = poketraceResult?.priceData || null;
  const poketraceId = poketraceResult?.poketraceId || null;
  const tcgplayerId = poketraceRaw?.refs?.tcgplayerId || poketraceRaw?.tcgplayerId || null;

  // 2. PARALLEL: TCGdex + JustTCG + Listings
  const [tcgdexSettled, justTCGSettled, listingsSettled] = await Promise.allSettled([
    tcgdexCardId ? fetchTCGdexPricing(tcgdexCardId) : Promise.resolve(null),
    tcgplayerId ? fetchJustTCGPricing(tcgplayerId, variant) : Promise.resolve(null),
    poketraceId ? fetchPoketraceListings(poketraceId) : Promise.resolve([]),
  ]);

  const tcgdex = tcgdexSettled.status === 'fulfilled' ? tcgdexSettled.value : null;
  const justTCG = justTCGSettled.status === 'fulfilled' ? justTCGSettled.value : null;
  const listings = listingsSettled.status === 'fulfilled' ? listingsSettled.value : [];

  console.log('[MultiSource] Individual listings:', listings.length);

  // 3. Transform Poketrace into per-condition breakdown
  const poketrace = extractPoketraceRaw(poketraceRaw);

  console.log('[MultiSource] Poketrace:', poketrace ? Object.keys(poketrace.raw).length + ' conditions' : 'null');
  console.log('[MultiSource] TCGdex:', tcgdex ? 'available' : 'null');
  console.log('[MultiSource] JustTCG:', justTCG ? Object.keys(justTCG.conditions).length + ' conditions' : 'null');

  // 4. SEQUENTIAL: Gemini analysis with all sources + individual listings
  const gemini = await callGeminiPricingAnalysis({
    cardName, cardNumber, setName, variant,
    poketrace, tcgdex, justTCG, listings,
  });

  console.log('[MultiSource] Gemini:', gemini ? `confidence: ${gemini.confidence}` : 'null');

  // 5. Compute best price
  const bestPrice = computeBestPrice(poketrace, tcgdex, gemini);

  console.timeEnd(perfLabel);
  return {
    poketrace,
    tcgdex,
    justTCG,
    gemini,
    bestPrice,
    listings,
    fetchedAt: new Date().toISOString(),
  };
}

export { RAW_TIERS, GRADE_SORT, CONDITION_MAP, CONDITION_MULTIPLIERS, LEGACY_CONDITION_MULTIPLIERS };
